import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload } from "lucide-react";
import { DocumentUpload } from "@/components/ui/document-upload";
import { DocumentList } from "@/components/ui/document-list";
import { useDocuments } from "@/hooks/useDocuments";
import { useToast } from "@/hooks/use-toast";
import { useDocumentFolders } from "@/hooks/useDocumentFolders";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DocumentFolderTiles } from "@/components/ui/document-folder-tiles";

interface DocumentsTabProps {
  clientName: string;
  clientId: number;
}

const DocumentsTab = ({ clientName, clientId }: DocumentsTabProps) => {
  const ROOT_FOLDER_VALUE = "root";
  const { toast } = useToast();

  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderVisibleToEmployees, setNewFolderVisibleToEmployees] =
    useState(false);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [isSavingFolder, setIsSavingFolder] = useState(false);
  const { folders, createFolder, updateFolder, refreshFolders } =
    useDocumentFolders({
      ownerType: "client",
      clientId,
    });

  // Fetch documents to calculate counts client-side
  const {
    documents: allDocuments,
    loading: allDocumentsLoading,
  } = useDocuments({
    autoRefresh: true,
    ownerType: "client",
    clientId,
  });

  // Calculate folder counts from allDocuments
  const foldersWithCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    
    // Count documents per folder
    allDocuments.forEach((doc: any) => {
      const folderId = doc.folderId;
      if (folderId !== null && folderId !== undefined) {
        counts[folderId] = (counts[folderId] || 0) + 1;
      }
    });

    return folders.map((folder) => ({
      ...folder,
      documentCount: counts[folder.id] || 0,
    }));
  }, [folders, allDocuments]);

  const {
    documents,
    loading: documentsLoading,
    error: documentsError,
    refreshDocuments,
  } = useDocuments({
    autoRefresh: true,
    ownerType: "client",
    clientId,
    folderId:
      selectedFolderId && selectedFolderId > 0 ? selectedFolderId : undefined,
    unfiled: selectedFolderId === null,
  });

  const applyFolderFilter = (folderValue: string) => {
    if (folderValue === ROOT_FOLDER_VALUE) setSelectedFolderId(null);
    else setSelectedFolderId(parseInt(folderValue, 10));
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || isSavingFolder) return;
    try {
      setIsSavingFolder(true);
      await createFolder(newFolderName.trim(), null, newFolderVisibleToEmployees);
      await refreshFolders();
      toast({ title: "Folder created", variant: "success" });
      setIsCreateFolderOpen(false);
      setNewFolderName("");
      setNewFolderVisibleToEmployees(false);
    } catch (e: any) {
      toast({
        title: "Create failed",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingFolder(false);
    }
  };

  const moveToFolder = async (documentId: number, folderId: number | null) => {
    const res = await fetch(`/api/documents/${documentId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ folderId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to move");
    }
    await refreshDocuments();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Documents for {clientName}</h3>
        <DocumentUpload
          trigger={
            <PermissionButton
              permission="documents:upload"
              whenDenied="hide"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </PermissionButton>
          }
          onUploadSuccess={async () => {
            await refreshDocuments();
          }}
          ownerType="client"
          clientId={clientId}
          folderId={
            selectedFolderId && selectedFolderId > 0
              ? selectedFolderId
              : undefined
          }
          folders={folders}
        />
      </div>

      <Card>
        <div className="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Folder</Label>
            <Select
              value={
                selectedFolderId === null
                  ? ROOT_FOLDER_VALUE
                  : String(selectedFolderId)
              }
              onValueChange={applyFolderFilter}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Root folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ROOT_FOLDER_VALUE}>Root folder</SelectItem>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <PermissionButton
              permission="documents:folders"
              whenDenied="hide"
              size="sm"
              variant="outline"
              onClick={() => setIsCreateFolderOpen(true)}
            >
              New Folder
            </PermissionButton>
          </div>
        </div>
      </Card>

      {/* Folder tiles */}
      <DocumentFolderTiles
        folders={foldersWithCounts}
        onOpenFolder={(f) => {
          // Update selectedFolderId to use existing filter, which will update dropdown
          setSelectedFolderId(f.id);
        }}
        onToggleEmployeeVisibility={async (folderId, visible) => {
          try {
            await updateFolder(folderId, { visibleToEmployees: visible });
            toast({
              title: visible
                ? "Folder shared with employees"
                : "Folder hidden from employees",
              variant: "success",
            });
          } catch (e: any) {
            toast({
              title: "Update failed",
              description: e.message,
              variant: "destructive",
            });
          }
        }}
      />

      {documentsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 text-sm">
            Error loading documents: {documentsError}
          </p>
        </div>
      )}

      {documentsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <DocumentList
          documents={documents as any}
          onDocumentUpdate={refreshDocuments}
          showActions={true}
          showEmployeeVisibility
          folders={folders}
          onMoveToFolder={moveToFolder}
          {...(selectedFolderId === null && folders.length > 0
            ? {
                emptyStateTitle: "No unfiled documents",
                emptyStateDescription:
                  "Your documents are organized in the folders above. Open a folder to see them, or upload a new document.",
              }
            : {})}
          emptyStateAction={
            <DocumentUpload
              trigger={
                <PermissionButton
                  permission="documents:upload"
                  whenDenied="hide"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </PermissionButton>
              }
              onUploadSuccess={async () => {
                await refreshDocuments();
              }}
              ownerType="client"
              clientId={clientId}
              folderId={
                selectedFolderId && selectedFolderId > 0
                  ? selectedFolderId
                  : undefined
              }
              folders={folders}
            />
          }
        />
      )}


      <Dialog
        open={isCreateFolderOpen}
        onOpenChange={(open) => {
          setIsCreateFolderOpen(open);
          if (!open) {
            setNewFolderName("");
            setNewFolderVisibleToEmployees(false);
            setIsSavingFolder(false);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="client-new-folder-name">Folder name</Label>
            <Input
              id="client-new-folder-name"
              placeholder="e.g. Contracts"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              autoFocus
            />
            <div className="flex items-center gap-2">
              <Checkbox
                id="client-new-folder-visible-to-employees"
                checked={newFolderVisibleToEmployees}
                onCheckedChange={(checked) =>
                  setNewFolderVisibleToEmployees(checked === true)
                }
              />
              <Label htmlFor="client-new-folder-visible-to-employees">
                Visible to employees
              </Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsCreateFolderOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || isSavingFolder}
            >
              {isSavingFolder ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default DocumentsTab;
